import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CalendarDays, Repeat } from "lucide-react";
import { Field, FieldLabel, Input, Select, Switch, Typography } from "@akuna/ui";
import { DateFieldPopover } from "@/components/ui/date-field-popover";
import { generateRecurrenceOccurrences, type RecurrenceFrequency } from "@/lib/recurrence";
import { cn } from "@/lib/utils";
import type { RecurrenceFrequencyOption, ReservationFormValues } from "./reservationForm";

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

function fromDateInput(value: string | null | undefined) {
  return value ? new Date(`${value}T12:00:00`) : undefined;
}

function toDateInput(date: Date | undefined) {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Mapeia a frequência da UI para a regra do gerador de ocorrências. */
function toRule(frequency: RecurrenceFrequencyOption): { frequency: RecurrenceFrequency; interval: number } {
  if (frequency === "monthly") return { frequency: "monthly", interval: 1 };
  if (frequency === "biweekly") return { frequency: "weekly", interval: 2 };
  return { frequency: "weekly", interval: 1 };
}

export function ReservationRecurrence({ form, showToggle = true }: { form: UseFormReturn<ReservationFormValues>; showToggle?: boolean }) {
  const { t, i18n } = useTranslation("daycareHotel");
  if (form.watch("moduleType") !== "day_care") return null;

  const recurrence = form.watch("recurrence");
  const checkInDate = form.watch("checkInDate");
  const weekdays = recurrence?.weekdays ?? [];
  const locale = i18n.language.startsWith("pt") ? "pt-BR" : "en-US";

  const patch = (next: Partial<NonNullable<ReservationFormValues["recurrence"]>>) =>
    form.setValue("recurrence", { frequency: recurrence?.frequency ?? "weekly", interval: recurrence?.interval ?? 1, weekdays, monthday: recurrence?.monthday ?? null, endsOn: recurrence?.endsOn ?? null, ...next }, { shouldValidate: true });

  const toggleDay = (day: number) => patch({ weekdays: weekdays.includes(day) ? weekdays.filter((d) => d !== day) : [...weekdays, day].sort() });

  // Prévia das próximas 5 datas.
  let preview: string[] = [];
  if (recurrence && checkInDate) {
    const start = new Date(`${checkInDate}T12:00:00`);
    const end = recurrence.endsOn ? new Date(`${recurrence.endsOn}T12:00:00`) : new Date(start.getTime() + 1000 * 60 * 60 * 24 * 180);
    const rule = toRule(recurrence.frequency);
    const usesWeekdays = recurrence.frequency !== "monthly";
    if (!usesWeekdays || weekdays.length > 0) {
      try {
        preview = generateRecurrenceOccurrences(start, end, {
          frequency: rule.frequency,
          interval: rule.interval,
          weekdays: usesWeekdays ? weekdays : undefined,
          monthday: recurrence.frequency === "monthly" ? recurrence.monthday ?? start.getDate() : undefined,
          endsOn: recurrence.endsOn ? new Date(`${recurrence.endsOn}T12:00:00`) : undefined,
        })
          .slice(0, 5)
          .map((occurrence) => new Intl.DateTimeFormat(locale, { weekday: "short", day: "2-digit", month: "short" }).format(occurrence.startsAt));
      } catch {
        preview = [];
      }
    }
  }

  return (
    <section className={showToggle ? "grid gap-3 rounded-xl border border-border bg-card p-4" : "grid gap-3"}>
      {showToggle ? (
      <div className="flex items-center justify-between">
        <Typography variant="h5">
          {t("recurrence")}
        </Typography>
        <Switch
          aria-label={t("recurrence")}
          checked={!!recurrence}
          onCheckedChange={(checked) => form.setValue("recurrence", checked ? { frequency: "weekly", interval: 1, weekdays: [], monthday: null, endsOn: null } : null)}
        />
      </div>
      ) : null}

      {recurrence && (
        <div className="grid gap-3">
          <div data-testid="recurrence-primary-fields" className="grid gap-3 sm:grid-cols-2">
            <Select
              label={t("frequency")}
              value={recurrence.frequency}
              onValueChange={(value) => patch({ frequency: value as RecurrenceFrequencyOption })}
              options={[
                { value: "weekly", label: t("recurrenceWeekly") },
                { value: "biweekly", label: t("recurrenceBiweekly") },
                { value: "monthly", label: t("recurrenceMonthly") },
              ]}
            />
            <Field>
              <FieldLabel>{t("endsOn")}</FieldLabel>
              <DateFieldPopover
                selected={fromDateInput(recurrence.endsOn)}
                onSelect={(date) => patch({ endsOn: toDateInput(date) })}
                trigger={
                  <button
                    type="button"
                    aria-label={t("endsOn")}
                    aria-required="true"
                    className="flex h-10 w-full items-center gap-2 rounded-lg border border-border bg-background px-3 text-left text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <CalendarDays size={15} className="text-muted-foreground" />
                    <span className={recurrence.endsOn ? "" : "text-muted-foreground"}>
                      {recurrence.endsOn
                        ? new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short", year: "numeric" }).format(fromDateInput(recurrence.endsOn))
                        : t("selectDate")}
                    </span>
                  </button>
                }
              />
            </Field>
          </div>

          {recurrence.frequency !== "monthly" && (
            <div
              data-testid="weekly-recurrence-details"
              className="grid gap-3 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-start"
            >
              <Field>
                <FieldLabel>{t("weekdaysLabel")}</FieldLabel>
                <div className="flex flex-wrap gap-1.5">
                  {WEEKDAYS.map((day) => (
                    <button
                      key={day}
                      type="button"
                      className={`h-8 w-8 rounded-full border text-xs font-semibold transition-colors ${weekdays.includes(day) ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground"}`}
                      onClick={() => toggleDay(day)}
                    >
                      {t(`weekdayShort.${day}`)}
                    </button>
                  ))}
                </div>
              </Field>
              {preview.length > 0 && (
                <RecurrencePreview
                  dates={preview}
                  label={t("nextDates")}
                  className="sm:border-l sm:border-border sm:pl-4"
                />
              )}
            </div>
          )}

          {recurrence.frequency === "monthly" && (
            <div data-testid="monthly-recurrence-details" className="grid gap-3 sm:grid-cols-2">
              <Field>
                <FieldLabel>{t("monthDay")}</FieldLabel>
                <Input type="number" min={1} max={31} value={recurrence.monthday ?? ""} onChange={(event) => patch({ monthday: Number(event.target.value) || null })} />
              </Field>
              {preview.length > 0 && (
                <RecurrencePreview
                  dates={preview}
                  label={t("nextDates")}
                  className="sm:border-l sm:border-border sm:pl-4"
                />
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function RecurrencePreview({
  dates,
  label,
  className,
}: {
  dates: string[];
  label: string;
  className?: string;
}) {
  return (
    <div data-testid="recurrence-preview" className={cn("grid min-w-0 gap-1.5", className)}>
      <span className="akuna-field__label">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {dates.map((date) => (
          <Typography asChild variant="caption" key={date}>
            <span className="rounded-full bg-muted px-2.5 py-1 font-medium text-foreground">{date}</span>
          </Typography>
        ))}
      </div>
    </div>
  );
}
